/**
 * SRES · Classification Threshold Editor — Phase 1 · Feb 2026.
 *
 * CP-tunable JORC / NI 43-101 thresholds surfaced as a compact panel
 * that lives inside the CPR configuration flow.  Reads/writes:
 *
 *   GET  /api/projects/{pid}/classification/config
 *   PUT  /api/projects/{pid}/classification/config
 *
 * The saved config is picked up automatically by the Extended CPR
 * report engine (backend) — no additional plumbing required.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const RULE_OPTIONS = [
  { v: "distance",         l: "Distance to nearest composite" },
  { v: "kriging_variance", l: "Kriging variance quantile" },
  { v: "hybrid",           l: "Hybrid (kriging M/I, distance I/Inf)" },
];


function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10.5px] font-mono uppercase tracking-widest text-[#0B2545] mb-1">
        {label}
      </div>
      {children}
      {hint ? (
        <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>
      ) : null}
    </label>
  );
}


export default function ClassificationConfigPanel({ projectId }) {
  const qc = useQueryClient();
  const cfgQ = useQuery({
    queryKey: ["classification-config", projectId],
    queryFn: () =>
      api
        .get(`/projects/${projectId}/classification/config`)
        .then((r) => r.data),
    enabled: !!projectId,
  });

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (cfgQ.data && !form) setForm(cfgQ.data);
  }, [cfgQ.data, form]);

  const save = useMutation({
    mutationFn: (payload) =>
      api
        .put(`/projects/${projectId}/classification/config`, payload)
        .then((r) => r.data),
    onSuccess: () => {
      toast.success("Classification thresholds saved");
      qc.invalidateQueries({ queryKey: ["classification-config", projectId] });
    },
    onError: (e) => {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    },
  });

  if (!form) {
    return (
      <div
        className="rounded-none border border-[#0B2545]/20 bg-[#F7F2E7]/40 p-4"
        data-testid="classification-panel-loading"
      >
        <div className="text-[11.5px] text-[#0B2545] font-mono uppercase tracking-widest">
          Classification thresholds
        </div>
        <div className="mt-1 text-[12px] text-slate-500">Loading…</div>
      </div>
    );
  }

  const num = (k) => (
    <Input
      type="number"
      step="0.01"
      value={form[k] ?? 0}
      onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
      className="rounded-none border-[#0B2545]/30 focus:border-[#C99A3B] font-mono text-[13px]"
      data-testid={`class-cfg-${k.replace(/_/g, "-")}`}
    />
  );

  return (
    <section
      className="border border-[#0B2545]/25 bg-gradient-to-br from-[#F7F2E7]/70 to-white"
      data-testid="classification-panel"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#0B2545] text-white">
        <div className="flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#C99A3B]" />
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.28em] text-[#C99A3B]">
              JORC / NI 43-101 · CP-controlled
            </div>
            <div className="text-[13px] font-semibold text-white">
              Classification Thresholds
            </div>
          </div>
        </div>
        {form.is_default ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#C99A3B]/90">
            defaults
          </span>
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-1">
            <CheckCircle size={11} weight="fill" /> saved
          </span>
        )}
      </header>

      <div className="p-4 space-y-4">
        <Field label="Classification rule" testid="class-cfg-rule">
          <select
            value={form.rule_type}
            onChange={(e) => setForm({ ...form, rule_type: e.target.value })}
            className="w-full h-9 rounded-none border border-[#0B2545]/30 bg-white px-2 text-[13px] focus:border-[#C99A3B] focus:outline-none"
            data-testid="class-cfg-rule-select"
          >
            {RULE_OPTIONS.map((r) => (
              <option key={r.v} value={r.v}>{r.l}</option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Measured max distance (m)"
                 hint="Blocks with a composite within this radius are eligible for Measured">
            {num("measured_max_distance_m")}
          </Field>
          <Field label="Indicated max distance (m)"
                 hint="Beyond Indicated is classified Inferred">
            {num("indicated_max_distance_m")}
          </Field>
          <Field label="Measured min samples">
            {num("measured_min_samples")}
          </Field>
          <Field label="Indicated min samples">
            {num("indicated_min_samples")}
          </Field>
          <Field label="Kriging variance · Measured quantile"
                 hint="0.15 = elevate the lowest-variance 15 % of blocks">
            {num("kriging_measured_quantile")}
          </Field>
          <Field label="Kriging variance · Indicated quantile">
            {num("kriging_indicated_quantile")}
          </Field>
        </div>

        <Field label="CP notes (surfaced in the report)"
               hint="Explain the rationale — drill spacing, sample quality, geological confidence.">
          <Textarea
            value={form.cp_notes || ""}
            onChange={(e) => setForm({ ...form, cp_notes: e.target.value })}
            rows={3}
            className="rounded-none border-[#0B2545]/30 focus:border-[#C99A3B] font-mono text-[12px]"
            placeholder="e.g. Measured blocks confined to zones drilled at ≤50 m spacing with reciprocal QAQC..."
            data-testid="class-cfg-notes"
          />
        </Field>

        <div className="flex items-center gap-2 pt-2 border-t border-[#0B2545]/10">
          <Button
            type="button"
            onClick={() => save.mutate(form)}
            disabled={save.isPending}
            className="rounded-none bg-[#0B2545] hover:bg-[#0B2545]/90 text-white font-semibold px-4"
            data-testid="class-cfg-save"
          >
            {save.isPending ? (
              <>
                <ArrowsClockwise size={12} className="animate-spin mr-1" />
                Saving…
              </>
            ) : (
              "Save thresholds"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setForm(cfgQ.data)}
            className="rounded-none border-[#0B2545]/30 text-[#0B2545] hover:bg-[#0B2545]/5"
            data-testid="class-cfg-reset"
          >
            Reset
          </Button>
          <span className="ml-auto text-[10.5px] text-slate-500 font-mono">
            Picked up automatically by the Extended CPR
          </span>
        </div>
      </div>
    </section>
  );
}
